export default function ManeeshPortfolio() {
  const projects = [
    {
      title: "Autohaus AI Showcase",
      subtitle: "AI-powered automotive content automation platform",
      stack: ["Laravel", "OpenAI", "Gemini", "AWS"],
      points: [
        "Automates landing pages, newsletters, and media workflows for automotive campaigns.",
        "Designed around scalable prompt-driven architecture and reusable content systems.",
        "Showcases leadership, AI integration, and end-to-end product thinking."
      ]
    },
    {
      title: "PDF AI Generator",
      subtitle: "PDF to landing page automation workflow",
      stack: ["PHP", "Laravel", "Bootstrap", "OpenAI"],
      points: [
        "Extracts structured dealership data from PDFs and transforms it into marketing-ready pages.",
        "Reduces manual content preparation and speeds up delivery.",
        "Built for consistency, SEO-focused content, and repeatable generation workflows."
      ]
    },
    {
      title: "AI Media Generator",
      subtitle: "Image and video generation workflow",
      stack: ["Laravel", "Gemini", "Runway", "AJAX"],
      points: [
        "Generates images and videos from prompts with real-time progress workflows.",
        "Supports media automation for campaign assets and creative production.",
        "Focuses on usability, reliability, and modern AI integration."
      ]
    },
    {
      title: "MERN E-commerce App",
      subtitle: "Full-stack commerce platform",
      stack: ["MongoDB", "Express", "React", "Node.js"],
      points: [
        "Built authentication, cart, order, and admin workflows.",
        "Structured for scalable APIs and clean frontend interactions.",
        "Demonstrates strong full-stack engineering fundamentals."
      ]
    },
    {
      title: "WooCommerce Automation",
      subtitle: "Custom pricing and checkout automation",
      stack: ["WordPress", "WooCommerce", "PHP", "jQuery"],
      points: [
        "Implemented business-driven coupon, pricing, and checkout enhancements.",
        "Improved flexibility for real-world e-commerce workflows.",
        "Highlights practical plugin customization and conversion-focused solutions."
      ]
    }
  ];

  const skills = [
    "Laravel",
    "PHP",
    "MERN Stack",
    "React.js",
    "Vue.js",
    "Node.js",
    "MySQL",
    "MongoDB",
    "Bootstrap",
    "AWS",
    "AI Integrations",
    "Automation Systems"
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.16),transparent_30%),radial-gradient(circle_at_top_left,rgba(168,85,247,0.14),transparent_28%)]" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
          <div className="grid items-center gap-10 lg:grid-cols-[1.3fr_0.7fr]">
            <div>
              <div className="mb-4 inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200">
                Team Lead • Senior Software Engineer • 13+ Years Experience
              </div>
              <h1 className="max-w-4xl text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
                Building scalable products with <span className="text-cyan-300">Laravel</span>, <span className="text-fuchsia-300">MERN</span>, and <span className="text-emerald-300">AI automation</span>.
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-300 md:text-xl">
                I’m Maneesh Tiwari, a Team Lead at VirtualEmployee focused on AI-powered platforms,
                automation systems, and high-performance web applications. My work blends product thinking,
                scalable architecture, and practical delivery for real business use cases.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="mailto:er.maneeshtiwari@gmail.com"
                  className="rounded-2xl bg-cyan-400 px-6 py-3 font-medium text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:-translate-y-0.5"
                >
                  Contact Me
                </a>
                <a
                  href="https://github.com/ermaneeshtiwari"
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-2xl border border-white/15 px-6 py-3 font-medium text-white/90 transition hover:bg-white/5"
                >
                  GitHub Profile
                </a>
                <a
                  href="https://www.linkedin.com/in/maneeshtiwari1/"
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-2xl border border-white/15 px-6 py-3 font-medium text-white/90 transition hover:bg-white/5"
                >
                  LinkedIn
                </a>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              <div className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur">
                <p className="text-sm uppercase tracking-[0.2em] text-slate-400">Current Role</p>
                <p className="mt-3 text-2xl font-semibold">Team Lead</p>
                <p className="mt-2 text-slate-300">VirtualEmployee • Delhi NCR</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur">
                <p className="text-sm uppercase tracking-[0.2em] text-slate-400">Focus Areas</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["AI Platforms", "Automation", "Laravel", "MERN", "Architecture"].map((item) => (
                    <span key={item} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-slate-200">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur">
                <p className="text-sm uppercase tracking-[0.2em] text-slate-400">Selected Strengths</p>
                <ul className="mt-4 space-y-2 text-slate-300">
                  <li>• Clean architecture</li>
                  <li>• AI integrations</li>
                  <li>• Product delivery</li>
                  <li>• Scalable workflows</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section className="mx-auto max-w-7xl px-6 py-16 md:px-10">
        <div>
          <p className="text-sm font-medium uppercase tracking-[0.25em] text-cyan-300">Screenshots</p>
          <h2 className="mt-3 text-3xl font-semibold md:text-4xl">UI Preview</h2>

          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              "/screenshots/auto.png",
              "/screenshots/pdf.png",
              "/screenshots/ai-media.png",
              "/screenshots/eshop.png",
              "/screenshots/woocom.png",
              "/screenshots/ai-media-video.png"
            ].map((img, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-white/10 bg-slate-900">
                <img src={img} alt={`screenshot-${i}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 md:px-10">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.25em] text-cyan-300">About</p>
            <h2 className="mt-3 text-3xl font-semibold md:text-4xl">A developer profile built around real product outcomes.</h2>
          </div>
          <div className="text-lg leading-8 text-slate-300">
            <p>
              My experience spans backend systems, frontend applications, AI-powered workflows, and business-focused
              automation. I work across Laravel, PHP, MERN, WordPress, and modern API integrations to build tools that
              save time, improve consistency, and support scalable delivery.
            </p>
            <p className="mt-4">
              I enjoy turning complex requirements into structured, maintainable systems—from PDF extraction and content
              generation pipelines to media automation and e-commerce customizations.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-8 md:px-10">
        <div className="flex items-end justify-between gap-6">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.25em] text-fuchsia-300">Projects</p>
            <h2 className="mt-3 text-3xl font-semibold md:text-4xl">Selected work</h2>
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {projects.map((project, index) => (
            <div key={project.title} className="rounded-3xl border border-white/10 bg-slate-900/80 p-7 shadow-xl shadow-black/20">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm text-slate-400">Project {String(index + 1).padStart(2, "0")}</p>
                  <h3 className="mt-2 text-2xl font-semibold">{project.title}</h3>
                  <p className="mt-2 text-slate-300">{project.subtitle}</p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {project.stack.map((item) => (
                  <span key={item} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-slate-200">
                    {item}
                  </span>
                ))}
              </div>
              <ul className="mt-6 space-y-3 text-slate-300">
                {project.points.map((point) => (
                  <li key={point} className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-cyan-300" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 md:px-10">
        <div className="rounded-[2rem] border border-white/10 bg-white/5 p-8 md:p-10">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="text-sm font-medium uppercase tracking-[0.25em] text-emerald-300">Skills</p>
              <h2 className="mt-3 text-3xl font-semibold md:text-4xl">Core technologies</h2>
              <p className="mt-4 text-slate-300">
                A balanced mix of backend engineering, frontend delivery, cloud tools, and AI integration.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {skills.map((skill) => (
                <span
                  key={skill}
                  className="rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-sm font-medium text-slate-100 shadow-lg"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-6 md:px-10 md:pb-20">
        <div className="rounded-[2rem] border border-cyan-400/20 bg-gradient-to-br from-cyan-400/10 via-slate-900 to-fuchsia-400/10 p-8 md:p-10">
          <div className="grid gap-8 md:grid-cols-[1.2fr_0.8fr] md:items-center">
            <div>
              <p className="text-sm font-medium uppercase tracking-[0.25em] text-cyan-200">Let’s connect</p>
              <h2 className="mt-3 text-3xl font-semibold md:text-4xl">Need a developer who can combine engineering, automation, and product thinking?</h2>
              <p className="mt-4 max-w-2xl text-slate-300">
                I’m open to discussing portfolio opportunities, leadership-oriented roles, and product-focused engineering work.
              </p>
            </div>
            <div className="space-y-4 rounded-3xl border border-white/10 bg-slate-950/40 p-6 backdrop-blur">
              <div>
                <p className="text-sm text-slate-400">Email</p>
                <a href="mailto:er.maneeshtiwari@gmail.com" className="mt-1 block text-lg font-medium text-white hover:text-cyan-300">
                  er.maneeshtiwari@gmail.com
                </a>
              </div>
              <div>
                <p className="text-sm text-slate-400">GitHub</p>
                <a href="https://github.com/ermaneeshtiwari" target="_blank" rel="noreferrer" className="mt-1 block text-lg font-medium text-white hover:text-cyan-300">
                  github.com/ermaneeshtiwari
                </a>
              </div>
              <div>
                <p className="text-sm text-slate-400">LinkedIn</p>
                <a href="https://www.linkedin.com/in/maneeshtiwari1/" target="_blank" rel="noreferrer" className="mt-1 block text-lg font-medium text-white hover:text-cyan-300">
                  linkedin.com/in/maneeshtiwari1
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
